import { Row, Button, Form, Col } from "react-bootstrap";
import { RootState } from "redux/store";
import { useCallback, useEffect, useState } from "react";
import { BulkEdit } from "./Popups/BulkEdit";
import { Spinner, Table, Modal, TooltipIcon } from "components";
import { getStores } from "redux/slices/Stores";
import { useDisplayError } from "helpers/Functions";
import { getDepartments } from "redux/slices/Departments";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { displayTooltipsIcons } from "helpers/data/dataHandling";
import { updateAssignPriceGroupField, getPriceGroupsStore, bulkUpdatePriceGroupsStore, GetCurrentPriceGroup, applyPriceGroupsStore, updateAssignPriceGroupTableField } from "redux/slices/AssignPriceGroup";

type departmentProps = {
    display: boolean;
    updateTab: (tab: string) => void;
}

export default function Department({ display, updateTab }: departmentProps) {
    const dispatch = useAppDispatch();
    const displayError = useDisplayError();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [bulkEditShow, setBulkEditShow] = useState(false);
    const [disableModal, setDisableModal] = useState(false);
    const stores = useAppSelector((state: RootState) => state.stores);
    const priceGroups = useAppSelector((state: RootState) => state.priceGroups);
    const departments = useAppSelector((state: RootState) => state.department);
    const assignPriceGroup = useAppSelector((state: RootState) => state.assignPriceGroup);
    const user = useAppSelector((state: RootState) => state.logedinUser);
    const [currentDepartmentDetails, setCurrentStoreDetails] = useState({
        departmentId: 0,
        storeGroupId: 0,
        managedStockId: 0,
        newPriceGroupId: 0,
        departmentName: "",
    });
    const columns = [
        {
            Header: "Name",
            accessor: "departmentName",
            defaultCanSort: true,
            width: 200,
            Cell: (row: any) => <a className="link" onClick={() => moveToStocks(row.cell.row.original.departmentId)}>{row.value}</a>
        },
        {
            Header: "Current Price Group",
            accessor: "priceGroupName",
            defaultCanSort: true,
            width: 200,
            Cell: (row: any) => <span>{row.value}{displayTooltipsIcons(row.value)}</span>
        },
        {
            Header: "New Price Group",
            accessor: "newpriceLevelId",
            defaultCanSort: true,
            width: 800,
            Cell: (row: any) => <PriceGroupDropdown id={row.cell.row.original.newPriceGroupId} data={row.cell.row.original} />,
        },
    ];

    function moveToStocks(departmentId: number) {
        updateTab("3");
        dispatch(updateAssignPriceGroupField({ key: "currentDepartment", value: departmentId }));
        dispatch(updateAssignPriceGroupField({ key: "currentDepartmentPriceGroup", value: GetCurrentPriceGroup(assignPriceGroup.currentStore) }));
    }

    useEffect(() => {
        dispatch(getStores({}));
    }, []);

    useEffect(() => {
        if (display === true && assignPriceGroup.currentStore > 0 && assignPriceGroup.activeTab === 2) getDepartmentsList();
    }, [assignPriceGroup.currentStore, display, assignPriceGroup.activeTab]);

    async function getDepartmentsList() {
        if (assignPriceGroup.currentStore > 0) {
            setLoading(true);
            await dispatch(getDepartments());
            if (stores.table.length > 0) {
                await dispatch(getPriceGroupsStore({
                    storeIds: [assignPriceGroup.currentStore],
                    groupBy: "Departments"
                })).then((res) => {
                    if (res.data) {
                        let departments: any[] = [];
                        let departmentIds: any[] = [];
                        res.data.forEach((department: any) => {
                            if (!departmentIds.includes(department.departmentId)) {
                                departments.push(department);
                                departmentIds.push(department.departmentId);
                            }
                        });
                        dispatch(updateAssignPriceGroupField({ key: "departments", value: departments }));
                    }
                });
            }
            setLoading(false);
        }
    }

    async function handleSubmit() {
        setDisableModal(true);
        setSaving(true);
        let managedDepartmentIds: number[] = [];
        if (assignPriceGroup.selecetedDepartments.length === 0) {
            managedDepartmentIds = assignPriceGroup.departments.map((department: any) => department.departmentId);
        } else {
            //@ts-ignore
            managedDepartmentIds = assignPriceGroup.selecetedDepartments.map((departmentId: number) => assignPriceGroup.departments[departmentId].departmentId);
        }
        let payload: any = {
            filter: {
                storeIds: [assignPriceGroup.currentStore],
                managedDepartmentIds: managedDepartmentIds,
            },
            sync: true,
        }
        // console.log("payload", payload);
        await dispatch(applyPriceGroupsStore(payload)).then((res) => {
            setShowModal(false);
            setDisableModal(false);
            setSaving(false);

            if (res.data) {
                getDepartmentsList();
            }
        });
    }

    const PriceGroupDropdown = (props: any) => {
        const tooltipType: string = props.id === -1 ? "Base Sell Price" : props.id === -2 ? "Mixed" : props.id;
        const [departmentValue, setDepartmentValue] = useState(props.id ? props.id : "Mixed");
        const [loadingValue, setLoadingValue] = useState(false);
        async function handleDepartmentUpdate(newPriceGroupId: number) {
            setLoadingValue(true);
            await dispatch(bulkUpdatePriceGroupsStore({
                managedStockFilter: {
                    departmentIds: [props.data.departmentId],
                },
                newPriceGroupId: newPriceGroupId,
                storeIds: [assignPriceGroup.currentStore]
            })).then((res) => {
                if (res.data) {
                    setDepartmentValue(newPriceGroupId);
                    const index = assignPriceGroup.departments.findIndex((department) => department.departmentId === props.data.departmentId);
                    dispatch(updateAssignPriceGroupTableField({ field: "departments", index: index, key: "newPriceGroupId", value: newPriceGroupId }));
                }
            }).catch((error: any) => {
                displayError(error);
            });
            setLoadingValue(false);
        }
        return (
            <div style={{ maxWidth: "250px", display: "flex" }}>
                {loadingValue ?
                    <span><Spinner className="spinner-border-sm me-1" tag="span" color="primary" />Saving... </span>
                    :
                    <>
                        <Form.Group style={{ margin: "-3px 0px" }}>
                            <Form.Select
                                disabled={!user.permissions.assignPriceGroups.canUpdate}
                                className="custom-select"
                                value={departmentValue}
                                onChange={(e) => handleDepartmentUpdate(parseInt(e.target.value))}>
                                <option value={0}></option>
                                <option value={-1}>Base Sell Price</option>
                                <option value={-2} hidden>Mixed</option>
                                {priceGroups.table.map((priceLevel: any, index: any) => {
                                    return <option key={index} value={priceLevel.id}>{priceLevel.name}</option>
                                })}
                            </Form.Select>
                        </Form.Group>
                        {displayTooltipsIcons(tooltipType)}
                    </>
                }
            </div>
        );
    };

    async function handleDepartmentPriceGroupUpdate() {
        await bulkUpdateDepartmentPriceGroups([currentDepartmentDetails.departmentId], currentDepartmentDetails.newPriceGroupId, [assignPriceGroup.currentStore]);
        getDepartmentsList();
    }

    async function bulkUpdateDepartmentPriceGroups(departmentId: number[], newPriceGroupId: number, storeIds: number[]) {
        await dispatch(bulkUpdatePriceGroupsStore({
            managedStockFilter: {
                departmentIds: departmentId,
            },
            newPriceGroupId: newPriceGroupId,
            storeIds: storeIds
        })).then((res) => {
        }).catch((error: any) => {
            displayError(error);
        });
    }

    const selectedRows = useCallback((pageIndex: any) => {
        dispatch(updateAssignPriceGroupField({ key: "selecetedDepartments", value: pageIndex }));
    }, []);

    function TopbarChildComponent() {
        return (
            <>{assignPriceGroup.selecetedDepartments.length > 1 &&
                <Button variant="light" className="me-1" onClick={() => setBulkEditShow(true)}><i className="mdi mdi-pencil"></i> Bulk Edit</Button>
            }</>
        );
    }

    function getPriceGroupName(currentStore: number) {
        let priceGroupName = "";
        assignPriceGroup.stores.forEach((store: any) => {
            if (store.storeId === currentStore) priceGroupName = store.priceGroupName;
        });
        dispatch(updateAssignPriceGroupField({ key: "currentStorePriceGroup", value: priceGroupName }));
    }

    async function saveBulkEdit(priceGroupId: number) {
        //@ts-ignore
        const storeIds = assignPriceGroup.selectedStores.map((storeId: number) => assignPriceGroup.stores[storeId].storeId);
        let managedDepartmentIds: number[] = [];
        if (assignPriceGroup.selecetedDepartments.length === 0) {
            managedDepartmentIds = assignPriceGroup.departments.map((department: any) => department.departmentId);
        } else {
            //@ts-ignore
            managedDepartmentIds = assignPriceGroup.selecetedDepartments.map((departmentId: number) => assignPriceGroup.departments[departmentId].departmentId);
        }
        await bulkUpdateDepartmentPriceGroups(managedDepartmentIds, priceGroupId, [assignPriceGroup.currentStore]);
        await getDepartmentsList();
        setBulkEditShow(false);
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={3}>
                    <Form.Group>
                        <div className="" style={{ display: "flex" }}>
                            <Col xs={11}>
                                <Form.Label className="form-check-label">Store</Form.Label>
                                <Form.Select
                                    className="custom-select"
                                    value={assignPriceGroup.currentStore}
                                    onChange={(e) => {
                                        dispatch(updateAssignPriceGroupField({ key: "currentStore", value: parseInt(e.target.value) }));
                                        getPriceGroupName(parseInt(e.target.value));
                                    }}
                                >
                                    <option hidden value="0">Choose a Store</option>
                                    {stores.table.map((priceLevel: any, index: any) => {
                                        return <option key={index} value={priceLevel.id}>{priceLevel.name}</option>
                                    })}
                                </Form.Select>
                            </Col>
                            <Col xs={1} style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }} className="d-flex ">
                                <div className="m1" style={{ marginBottom: "4px" }}>
                                    <TooltipIcon text="Showing departments with stock lines" placement="right" />
                                </div>
                            </Col>
                        </div>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-1 ps-1 pe-1">
                <div className="page-container">
                    {bulkEditShow &&
                        <BulkEdit
                            show={bulkEditShow}
                            saveSelected={saveBulkEdit}
                            handleClose={() => setBulkEditShow(false)}
                            count={assignPriceGroup.selecetedDepartments.length}
                        />}
                    <Modal
                        title="Confirm - Department level"
                        show={showModal}
                        buttonType="primary"
                        disableButtons={disableModal}
                        buttonText={disableModal ? "Applying..." : "Continue"}
                        handleClose={() => setShowModal(false)}
                        handleSubmit={handleSubmit}
                        description={
                            <>
                                <p>You are about to apply the new price group and sync the new sell prices.</p>
                                <p>Are you sure you want to continue?</p>
                            </>
                        }
                    />
                    <Table
                        pageSize={100}
                        maxHeight={500}
                        isSortable={true}
                        pagination={true}
                        isSelectable={true}
                        isSearchable={true}
                        disableExport={true}
                        columns={columns}
                        loading={loading}
                        errors={assignPriceGroup.currentStore > 0 ? assignPriceGroup.error : "Please select a store to continue."}
                        reloadTableRows={() => getDepartmentsList()}
                        data={assignPriceGroup.departments}
                        selectedRows={selectedRows}
                        topbarChild={<TopbarChildComponent />}
                        className="table-dropdown"
                    />
                </div>
            </Row>
            <Row className="mt-3 mb-2">
                <Col sm={6}>
                </Col>
                <Col sm={6} className="d-flex flex-row-reverse">
                    <Button variant="primary" className="btn ms-1 cta-button" onClick={() => { setShowModal(true); setDisableModal(false); }} disabled={!user.permissions.assignPriceGroups.canUpdate}>
                        {saving ? <Spinner className="spinner-border-sm me-1" tag="span" color="white" /> : null}
                        {saving ? "Applying..." : "Apply & Sync"}
                    </Button>
                    <Button variant="light" className="btn cta-button" onClick={() => dispatch(updateAssignPriceGroupField({ key: "activeTab", value: 3 }))}>Review Prices</Button>
                </Col>
            </Row>
        </>
    );
}